import { useEffect, useState } from 'react';
// Déclaration pour TypeScript (gtag est global)
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Configuration simplifiée
interface AnalyticsConfig {
  gaTrackingId: string;
  gadsTrackingId: string;
  gtmTrackingId: string;
}

// Récupérer la config depuis les variables d'environnement
const analyticsConfig: AnalyticsConfig = {
  gaTrackingId: process.env.GATSBY_GA_TRACKING_ID || ``,
  gadsTrackingId: process.env.GATSBY_GADS_TRACKING_ID || ``,
  gtmTrackingId: process.env.GATSBY_GTM_TRACKING_ID || ``,
};

// Utilitaire pour charger le script Google Analytics
export const loadGtagScript = (gaTrackingId: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Vérifier si le script existe déjà
    if (document.querySelector(`script[src*="gtag/js?id=${gaTrackingId}"]`)) {
      console.log(`GA script already loaded`);
      return resolve();
    }
    try {
      // Créer le script principal gtag.js
      const mainScript = document.createElement(`script`);
      mainScript.async = true;
      mainScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaTrackingId}`;
      // Événement de chargement réussi
      mainScript.onload = () => {
        // Initialiser gtag
        window.dataLayer = window.dataLayer || [];
        window.gtag = function () {
          window.dataLayer.push(arguments);
        };

        // Configuration de base
        window.gtag(`js`, new Date());
        window.gtag(`config`, gaTrackingId);

        console.log(`GA script loaded successfully`);
        resolve();
      };
      // Événement d'erreur
      mainScript.onerror = (error) => {
        console.error(`Failed to load GA script:`, error);
        reject(error);
      };
      // Ajouter à la page
      document.head.appendChild(mainScript);
    } catch (error) {
      console.error(`Error setting up GA:`, error);
      reject(error);
    }
  });
};
// Service d'analytics simplifié
export const useAnalyticsService = () => {
  const [hasConsent, setHasConsent] = useState<boolean>(false);
  const [isClient, setIsClient] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // Vérification de l'environnement côté client
  useEffect(() => {
    setIsClient(true);
    // Vérifier le consentement sauvegardé au chargement
    const savedConsent =
      typeof window !== `undefined` &&
      localStorage.getItem(`analytics-consent`) === `true`;
    setHasConsent(savedConsent || false);
  }, []);

  // Chargement des scripts GA4 et Google Ads si nécessaire (GTM est injecté via gatsby-ssr)
  useEffect(() => {
    if (!isClient || !hasConsent || isLoaded || isLoading) {
      return;
    }

    const { gaTrackingId, gadsTrackingId, gtmTrackingId } = analyticsConfig;

    if (gaTrackingId || gadsTrackingId) {
      setIsLoading(true);

      // Charger GA4 d'abord (pour les cookies)
      const loadPromises = [];

      if (gaTrackingId) {
        loadPromises.push(loadGtagScript(gaTrackingId));
      }

      if (gadsTrackingId) {
        loadPromises.push(loadGtagScript(gadsTrackingId));
      }

      Promise.all(loadPromises)
        .then(() => {
          console.log(`Analytics initialized successfully`);
          setIsLoaded(true);
          setIsLoading(false);

          // Tracker automatiquement la page courante après initialisation
          setTimeout(() => {
            const currentPath =
              window.location.pathname + window.location.search;
            console.log(`Auto-tracking initial page:`, currentPath);
            if (window.gtag) {
              window.gtag(`event`, `page_view`, {
                page_title: currentPath,
              });
            }
            if (gtmTrackingId && window.dataLayer) {
              window.dataLayer.push({
                event: `page_view`,
                page_path: currentPath,
              });
            }
          }, 100);
        })
        .catch((error) => {
          console.error(`Failed to initialize analytics:`, error);
          setIsLoading(false);
        });
    } else if (gtmTrackingId) {
      // GTM seul : pas de gtag à charger, on considère comme prêt pour les pushes dataLayer
      setIsLoaded(true);
      setTimeout(() => {
        const currentPath = window.location.pathname + window.location.search;
        if (window.dataLayer) {
          window.dataLayer.push({
            event: `page_view`,
            page_path: currentPath,
          });
        }
      }, 100);
    }
  }, [isClient, hasConsent, isLoaded, isLoading]);
  // Enregistrer la visite de page
  const trackPageView = (path?: string) => {
    console.log(`TRACK`, { isClient, hasConsent, isLoaded });
    if (!isClient || !hasConsent) {
      console.log(`Tracking skipped: client or consent requirement not met`);
      return;
    }
    if (!isLoaded) {
      console.log(`Tracking queued: GA not yet loaded`);
      // Option: stocker pour exécuter plus tard quand GA sera chargé
      return;
    }
    const page =
      path ||
      (typeof window !== `undefined`
        ? window.location.pathname + window.location.search
        : ``);
    console.log(`Tracking page view:`, page);
    if (
      typeof window !== `undefined` &&
      window.gtag &&
      (analyticsConfig.gaTrackingId || analyticsConfig.gadsTrackingId)
    ) {
      window.gtag(`event`, `page_view`, {
        page_title: page,
      });
    }
    // Push vers dataLayer pour GTM (navigation SPA)
    if (
      typeof window !== `undefined` &&
      analyticsConfig.gtmTrackingId &&
      window.dataLayer
    ) {
      window.dataLayer.push({
        event: `page_view`,
        page_path: page,
      });
    }
  };
  // Gérer le consentement
  const setConsent = (consent: boolean) => {
    localStorage.setItem(`analytics-consent`, String(consent));
    setHasConsent(consent);
    // Si le consentement est retiré, on pourrait ajouter une logique pour
    // désactiver le tracking (opt-out)
  };
  return {
    hasConsent,
    setConsent,
    trackPageView,
    isLoaded,
  };
};

// import { useEffect, useState } from 'react';
//
// // Déclaration pour TypeScript (gtag est global)
// declare global {
//   interface Window {
//     gtag: (...args: any[]) => void;
//     dataLayer: any[];
//   }
// }
//
// // Configuration simplifiée
// interface AnalyticsConfig {
//   gaTrackingId: string;
// }
//
// // Récupérer la config depuis les variables d'environnement
// const analyticsConfig: AnalyticsConfig = {
//   gaTrackingId: process.env.GATSBY_GA_TRACKING_ID || ''
// };
//
// // Utilitaire pour charger le script Google Analytics
// export const loadGtagScript = (gaTrackingId: string): Promise<void> => {
//   return new Promise((resolve, reject) => {
//     // Vérifier si le script existe déjà
//     if (document.querySelector(`script[src*="gtag/js?id=${gaTrackingId}"]`)) {
//       console.log('GA script already loaded');
//       return resolve();
//     }
//
//     try {
//       // Créer le script principal gtag.js
//       const mainScript = document.createElement('script');
//       mainScript.async = true;
//       mainScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaTrackingId}`;
//
//       // Événement de chargement réussi
//       mainScript.onload = () => {
//         // Initialiser gtag
//         window.dataLayer = window.dataLayer || [];
//         window.gtag = function() {
//           window.dataLayer.push(arguments);
//         };
//
//         // Configuration de base
//         window.gtag('js', new Date());
//         window.gtag('config', gaTrackingId, {
//           send_page_view: false // Important: désactiver le pageview automatique
//         });
//
//         console.log('GA script loaded successfully');
//         resolve();
//       };
//
//       // Événement d'erreur
//       mainScript.onerror = (error) => {
//         console.error('Failed to load GA script:', error);
//         reject(error);
//       };
//
//       // Ajouter à la page
//       document.head.appendChild(mainScript);
//     } catch (error) {
//       console.error('Error setting up GA:', error);
//       reject(error);
//     }
//   });
// };
//
// // Service d'analytics simplifié
// export const useAnalyticsService = () => {
//   const [hasConsent, setHasConsent] = useState<boolean>(false);
//   const [isClient, setIsClient] = useState<boolean>(false);
//   const [isLoaded, setIsLoaded] = useState<boolean>(false);
//   const [isLoading, setIsLoading] = useState<boolean>(false);
//
//   // Vérification de l'environnement côté client
//   useEffect(() => {
//     setIsClient(true);
//
//     // Vérifier le consentement sauvegardé au chargement
//     const savedConsent =
//       typeof window !== 'undefined' &&
//       localStorage.getItem('analytics-consent') === 'true';
//     setHasConsent(savedConsent || false);
//   }, []);
//
//   // Chargement du script GA4 si nécessaire
//   useEffect(() => {
//     if (!isClient || !hasConsent || isLoaded || isLoading) return;
//
//     const { gaTrackingId } = analyticsConfig;
//
//     if (gaTrackingId) {
//       setIsLoading(true);
//
//       loadGtagScript(gaTrackingId)
//         .then(() => {
//           console.log('GA initialized successfully');
//           setIsLoaded(true);
//           setIsLoading(false);
//
//           // Tracker automatiquement la page courante après initialisation
//           setTimeout(() => {
//             const currentPath = window.location.pathname + window.location.search;
//             console.log('Auto-tracking initial page:', currentPath);
//             window.gtag('event', 'page_view', {
//               page_title: currentPath
//             });
//           }, 100);
//         })
//         .catch((error) => {
//           console.error('Failed to initialize GA:', error);
//           setIsLoading(false);
//         });
//     }
//   }, [isClient, hasConsent, isLoaded, isLoading]);
//
//   // Enregistrer la visite de page
//   const trackPageView = (path?: string) => {
//     console.log('TRACK', { isClient, hasConsent, isLoaded });
//
//     if (!isClient || !hasConsent) {
//       console.log('Tracking skipped: client or consent requirement not met');
//       return;
//     }
//
//     if (!isLoaded) {
//       console.log('Tracking queued: GA not yet loaded');
//       // Option: stocker pour exécuter plus tard quand GA sera chargé
//       return;
//     }
//
//     const page =
//       path ||
//       (typeof window !== 'undefined'
//         ? window.location.pathname + window.location.search
//         : '');
//
//     console.log('Tracking page view:', page);
//
//     if (typeof window !== 'undefined' && window.gtag) {
//       window.gtag('event', 'page_view', {
//         page_title: page
//       });
//     } else {
//       console.error('gtag not available');
//     }
//   };
//
//   // Gérer le consentement
//   const setConsent = (consent: boolean) => {
//     localStorage.setItem('analytics-consent', String(consent));
//     setHasConsent(consent);
//
//     // Si le consentement est retiré, on pourrait ajouter une logique pour
//     // désactiver le tracking (opt-out)
//   };
//
//   return {
//     hasConsent,
//     setConsent,
//     trackPageView,
//     isLoaded
//   };
// };
